import { Injectable, signal } from '@angular/core';
import {
  FaInvite,
  FaInviteSessionState,
  FaProfile,
  MOCK_FA_PROFILES,
  sortFaProfiles,
} from './fa-invite.model';
import { ChatEvent } from './client-chat-thread.model';

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class FaInviteStateService {
  private readonly _store = signal<Record<string, FaInviteSessionState>>({});
  readonly storeSnapshot = this._store.asReadonly();

  /** All available FA profiles (same employer, sorted by availability). */
  readonly allProfiles: FaProfile[] = sortFaProfiles(MOCK_FA_PROFILES);

  private _ensureSession(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update(s => ({
      ...s,
      [sessionId]: { invite: null, joinedFaIds: [] },
    }));
  }

  getSessionState(sessionId: string): FaInviteSessionState {
    this._ensureSession(sessionId);
    return this._store()[sessionId];
  }

  getInviteStatus(sessionId: string, faId: string): 'none' | 'pending' | 'accepted' | 'declined' | 'already_joined' {
    // Read-only: do NOT call _ensureSession here (called from computed/template).
    const state = this._store()[sessionId];
    if (!state) return 'none';
    if (state.joinedFaIds.includes(faId)) return 'already_joined';
    if (!state.invite || state.invite.inviteeId !== faId) return 'none';
    return state.invite.status;
  }

  /**
   * Returns the two timeline events generated by sending an invite:
   * 1. Internal-only note (FA-facing): "You invited [Name]"
   * Returns them so the caller (thread state service) can inject them.
   */
  sendInvite(
    sessionId: string,
    faId: string,
    reason: string | undefined,
    inviterName: string
  ): ChatEvent {
    this._ensureSession(sessionId);
    const fa = MOCK_FA_PROFILES.find(p => p.id === faId);
    if (!fa) throw new Error(`Unknown FA id: ${faId}`);

    const invite: FaInvite = {
      inviteeId: faId,
      status: 'pending',
      reason: reason?.trim() || undefined,
      sentAt: new Date().toISOString(),
    };

    this._store.update(s => ({
      ...s,
      [sessionId]: { ...s[sessionId], invite },
    }));

    const reasonSuffix = invite.reason ? ` — "${invite.reason}"` : '';
    const noteEvent: ChatEvent = {
      id: `invite-note-${faId}-${Date.now()}`,
      eventType: 'conversation_note',
      createdAt: new Date().toISOString(),
      content: `${inviterName} invited ${fa.name} to join this conversation${reasonSuffix}`,
      speakerType: 'human_fa',
      speakerId: 'current-fa',
      speakerDisplayName: inviterName,
      speakerRoleLabel: 'Financial Assistant',
      avatarType: 'human_initials',
      visibility: 'internal',
      status: 'sent',
    };

    return noteEvent;
  }

  /**
   * Simulates the invitee accepting. Returns the client-visible join system event.
   */
  acceptInvite(sessionId: string, faId: string): ChatEvent | null {
    this._ensureSession(sessionId);
    const state = this._store()[sessionId];
    if (!state.invite || state.invite.inviteeId !== faId) return null;

    const fa = MOCK_FA_PROFILES.find(p => p.id === faId);
    if (!fa) return null;

    this._store.update(s => ({
      ...s,
      [sessionId]: {
        ...s[sessionId],
        invite: { ...s[sessionId].invite!, status: 'accepted', resolvedAt: new Date().toISOString() },
        joinedFaIds: [...s[sessionId].joinedFaIds, faId],
      },
    }));

    const joinEvent: ChatEvent = {
      id: `join-${faId}-${Date.now()}`,
      eventType: 'system_handoff_event',
      createdAt: new Date().toISOString(),
      content: `${fa.name} (Financial Assistant) joined the conversation`,
      speakerType: null,
      avatarType: null,
      visibility: 'client_visible',
      status: 'sent',
    };

    return joinEvent;
  }

  /**
   * Simulates the invitee declining.
   */
  declineInvite(sessionId: string, faId: string): void {
    this._ensureSession(sessionId);
    const state = this._store()[sessionId];
    if (!state.invite || state.invite.inviteeId !== faId) return;

    this._store.update(s => ({
      ...s,
      [sessionId]: {
        ...s[sessionId],
        invite: { ...s[sessionId].invite!, status: 'declined', resolvedAt: new Date().toISOString() },
      },
    }));
  }

  getJoinedFas(sessionId: string): FaProfile[] {
    const state = this.getSessionState(sessionId);
    return state.joinedFaIds
      .map(id => MOCK_FA_PROFILES.find(p => p.id === id))
      .filter((p): p is FaProfile => !!p);
  }
}
